/**
 * Sidebar's agent-review-queue pending-count nav badge (server-derived, BR-15). Invalidated on
 * `agent.draft_held` (BR-7 — the bridge forwards every arm, so a hold raised from ANY
 * tab/session/producer updates the badge, not just holds resolved on this queue page) and
 * whenever a queue item is resolved from AgentResponseQueuePage.
 *
 * Replaces the legacy `'agent-queue-count-changed'` `window` CustomEvent — a same-tab,
 * page-local refetch signal that predates the domain-event bus and could only ever move for
 * changes made in the current tab. This is the same pattern ET-BR-44 removed for the expense
 * review badge (`review-count-changed`), applied here for consistency (flagged across three
 * UI review cycles on this change).
 */

import { useQuery } from '@tanstack/react-query';

export const AGENT_REVIEW_QUEUE_PENDING_COUNT_KEY = ['agent-response-queue', 'pending-count'];

async function fetchAgentReviewQueuePendingCount(): Promise<number> {
  const response = await fetch('/landlord/api/agent-response-queue?status=pending_review', {
    credentials: 'include',
  });
  if (!response.ok) return 0;
  const data = (await response.json()) as { items: unknown[] };
  return data.items?.length ?? 0;
}

/** `enabled` should be gated on `agentic_support` (avoids 401/403 for unpermissioned managers). */
export function useAgentReviewQueuePendingCount(enabled: boolean) {
  return useQuery({
    queryKey: AGENT_REVIEW_QUEUE_PENDING_COUNT_KEY,
    queryFn: fetchAgentReviewQueuePendingCount,
    enabled,
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
}
